/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import EmberObject, { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import {
  byKeys,
  filterBy,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';

export default class AssessmentTable extends Component {
  // Services
  @service
  dates;

  @service
  session;

  @service
  store;

  @service
  terms;

  AssessmentDisplay = AssessmentDisplay;
  @tracked current = false;
  @tracked reportDate = null;

  @computed('terms.currentTerm', 'reportDate')
  get currentTerm() {
    if (isEmpty(this.reportDate)) {
      return this.terms.currentTerm;
    }
    return this.terms.currentTermAtDate(this.reportDate);
  }

  timeframes = [];

  // 24 for 12 months of this year + 12 months of previous year
  @tracked maxGradeColumns = 24;

  @tracked isParentStudentView = false;

  @computed(
    'assessment.{assessmentDisplay,noGradesColumnsToShowStaff,noGradesColumnsToShowCaregiversStudents}',
    'isParentStudentView',
    'timeframes.[]',
    'terms.terms.[]',
    'dates.{currentAcademicYear,previousAcademicYearLabel}',
    'currentTerm.term',
    'eligibleTimeframes.[]',
  )
  get gradeColumnsToShow() {
    if (this.isParentStudentView) {
      if (this.assessment.get('noGradesColumnsToShowCaregiversStudents')) {
        return this.assessment.get('noGradesColumnsToShowCaregiversStudents');
      }
      return 3;
    }
    if (this.assessment.get('noGradesColumnsToShowStaff')) {
      return this.assessment.get('noGradesColumnsToShowStaff');
    }
    switch (this.assessment.assessmentDisplay) {
      case AssessmentDisplay.MONTHS: {
        // Include this year's months and the previous year's months
        let monthsThisYear = moment().month() + 1;
        return monthsThisYear + 12;
      }
      case AssessmentDisplay.ALTERNATE:
        // All available past timeframes from this and last year
        return this.eligibleTimeframes.length;
      default: {
        // Always show the full previous year (4 terms) plus the current year's progress
        const termsPerYear = 4;
        let currentYearProgress = 0;
        if (!isEmpty(this.currentTerm)) {
          currentYearProgress = this.currentTerm.get('term') || termsPerYear;
        }
        let totalColumns = termsPerYear + currentYearProgress;
        return Math.min(this.maxGradeColumns, totalColumns);
      }
    }
  }

  @computed(
    'timeframes.@each.{startDateUnix,year}',
    'dates.{currentAcademicYear,currentAcademicYearLabel,previousAcademicYearLabel,currentDate}',
  )
  get eligibleTimeframes() {
    if (isEmpty(this.timeframes)) {
      return [];
    }
    let today = this.dates.currentDate
      ? moment(this.dates.currentDate, 'YYYY-MM-DD').endOf('day')
      : moment();
    let defaultYear = moment(today).year();
    let currentYear = this.dates.currentAcademicYear || defaultYear;
    let previousYear = currentYear - 1;
    let allowedYearValues = [
      currentYear,
      previousYear,
      this.dates.currentAcademicYearLabel,
      this.dates.previousAcademicYearLabel,
    ]
      .filter((value) => !isEmpty(value))
      .map((value) => value.toString());
    let eligible = this.timeframes.filter((timeframe) => {
      let timeframeYear = timeframe.get('year');
      let normalizedTimeframeYear = isEmpty(timeframeYear)
        ? null
        : timeframeYear.toString();
      if (
        isEmpty(normalizedTimeframeYear) ||
        !allowedYearValues.includes(normalizedTimeframeYear)
      ) {
        return false;
      }
      return timeframe.get('startDateUnix') <= today.unix();
    });
    return eligible.sort(byKeys('startDateUnix'));
  }

  @computed('gradeColumnsToShow', 'eligibleTimeframes.[]')
  get showTimeframes() {
    let timeframes = this.eligibleTimeframes;
    if (isEmpty(timeframes)) {
      return [];
    }
    let columnsToShow = Math.min(this.gradeColumnsToShow, timeframes.length);
    let startIndex = Math.max(0, timeframes.length - columnsToShow);
    return timeframes.slice(startIndex).reverse();
  }

  @computed('eligibleTimeframes.[]', 'assessment.assessmentDisplay')
  get timeframesDisplay() {
    return (
      this.get('assessment.assessmentDisplay') ===
        AssessmentDisplay.ALTERNATE && this.eligibleTimeframes.length >= 1
    );
  }

  @computed('gradeColumnsToShow')
  get showMonths() {
    const months = [];
    const today = moment();

    for (let i = 0; i < this.gradeColumnsToShow; i++) {
      const previousMonth = moment(today)
        .subtract(i, 'months')
        .startOf('month');
      months.push(previousMonth.format('MMM, YYYY'));
    }

    return months;
  }

  @computed('current', 'terms.terms.[]', 'currentTerm', 'gradeColumnsToShow')
  get showTerms() {
    if (this.current) {
      // Only show the current term - don't find the previous X terms.
      if (isEmpty(this.currentTerm)) {
        // Current term not loaded yet?
        return [];
      } else {
        return [this.currentTerm];
      }
    }
    let sorted = this.terms.terms.slice().sort(byKeys('start'));
    if (!isEmpty(this.currentTerm)) {
      let index = sorted.indexOf(this.currentTerm);
      let minIndex = Math.max(0, index - this.gradeColumnsToShow + 1);
      return sorted.slice(minIndex, index + 1).reverse();
    }
    return sorted.reverse();
  }

  @computed('currentTerm', 'terms.terms.[]', 'maxGradeColumns')
  get termsData() {
    let sorted = this.terms.terms.slice().sort(byKeys('start'));
    if (!isEmpty(this.currentTerm)) {
      let index = sorted.indexOf(this.currentTerm);
      let minIndex = Math.max(0, index - this.maxGradeColumns + 1);
      return sorted.slice(minIndex, index + 1).reverse();
    }
    return sorted.reverse();
  }

  @computed(
    'termsData.@each.start',
    'grades.@each.createTime',
    'assessment.assessmentDisplay',
    'showMonths',
    'showTimeframes.@each.{startDateUnix,endDateUnix,columnLabel}',
  )
  get data() {
    let terms = this.termsData;
    let months = this.showMonths;
    let timeframes = this.showTimeframes;
    let modules = this.get('assessment.modules');
    let grades = this.grades;
    let data = [];
    let gradesForMonth = EmberObject.create({});

    switch (this.get('assessment.assessmentDisplay')) {
      case AssessmentDisplay.MONTHS:
        grades.forEach(function (grade) {
          let gradeMonth = moment.unix(grade.createTime).format('MMM, YYYY');
          if (!gradesForMonth[gradeMonth]) {
            gradesForMonth[gradeMonth] = [grade];
          } else {
            gradesForMonth[gradeMonth].push(grade);
          }
        });
        months.forEach(function (month) {
          modules.forEach(function (module) {
            if (!isEmpty(gradesForMonth[month])) {
              let grade = filterBy(
                gradesForMonth[month],
                'module.id',
                module.id,
              )
                .sort(byKeys('createTime'))
                .at(-1);

              if (grade) {
                data.push({
                  month: moment.unix(grade.createTime).format('MMM, YYYY'),
                  moduleId: module.id,
                  grade,
                });
              }
            }
          });
        });
        break;
      case AssessmentDisplay.ALTERNATE:
        timeframes.forEach(function (timeframe) {
          modules.forEach(function (module) {
            let grade = filterBy(grades, 'module.id', module.id)
              .filter((g) => {
                return (
                  g.createTime >= timeframe.startDateUnix &&
                  g.createTime < timeframe.endDateUnix
                );
              })
              .sort(byKeys('createTime'))
              .at(-1);

            if (grade) {
              data.push({
                columnLabel: timeframe.columnLabel + timeframe.year,
                moduleId: module.id,
                grade,
              });
            }
          });
        });
        break;
      default:
        terms.forEach(function (term) {
          modules.forEach(function (module) {
            let grade = filterBy(grades, 'module.id', module.id)
              .filter((g) => {
                let next = term.get('next.start');
                let gradeDate = g.get('date');
                if (next) {
                  return term.get('start') <= gradeDate && gradeDate < next;
                }
                return term.get('start') <= gradeDate;
              })
              .sort(byKeys('createTime'))
              .at(-1);

            if (grade) {
              data.push({
                termId: term.id,
                moduleId: module.id,
                grade,
              });
            }
          });
        });
    }
    return data;
  }

  @computed('showTerms.[]', 'data.@each.termId')
  get termsWithData() {
    let data = this.data;
    return this.showTerms.filter((term) => {
      return findBy(data, 'termId', term.id);
    });
  }
}
